/**
 * @file consent-banner.ts
 * @description Вид баннера согласия на cookie — раздел `theme.consentBanner`
 * документа оформления (phase19.16, партия Р3г «Счётчики, баннер согласия на
 * cookie, TD-117», v0.19.62; site-builder.md SB134, SB94; проект данных
 * docs/phases/phase19.16-materials/db-design-r3g.md §2.3, решения §7.29
 * п. 1 и 11).
 *
 * Зачем в теме, а не в части COUNTERS: полоса или карточка, заливка, цвет
 * текста и ширина — это оформление. Дизайны (SB94) переносят тему целиком,
 * тёмный дизайн перекрашивает и баннер, а контраст проверяется тем же
 * правилом, что у корзины (`autoTextOnFillRatio`). Счётчики, режим и текст —
 * обработка данных, они в части COUNTERS (counters.ts).
 *
 * Подключение — как у `theme.cart`: ключ в `siteThemeSchema`, в
 * `SITE_THEME_SECTIONS` (без строки раздел молча отбросился бы при чтении) и
 * в `DEFAULT_SITE_THEME`. Версия схемы темы остаётся 2: раздел добавочный,
 * документам без него `mergeMissingKeys` дописывает умолчание.
 *
 * Умолчание — нынешняя полоса CookieBanner.tsx у всех магазинов (решение
 * §7.29 п. 11 «без заметных отличий после выкладки»): заливка «Плашки»
 * (`bg-site-panel`), текст «Авто» — на светлой плашке это «Текст»
 * (`text-site-text`), как сейчас.
 */

import { z } from "zod";

import { colorRefCss } from "./color-ref.js";
import { SITE_CONTRAST_TEXT_MIN } from "./contrast.js";
import { deepFreeze } from "./deep-freeze.js";
import { siteColorVar, type SiteColors } from "./keys.js";
import { resolveSurfaceFill, siteSurfaceFillSchema } from "./surface-fill.js";
import { siteAutoTextColorSchema, type SiteAutoTextColor } from "./text-color.js";
import { autoTextOnFillRatio, floorContrastTenth } from "./text-on-fill.js";
import { SITE_THEME_LIMITS } from "./theme-limits.js";

const L = SITE_THEME_LIMITS.consentBanner;

/** Вид баннера на ПК: полоса внизу или карточка в углу. На экране уже 640 px — всегда полоса. */
export const SITE_CONSENT_BANNER_LAYOUTS = ["bar", "card"] as const;
export type SiteConsentBannerLayout = (typeof SITE_CONSENT_BANNER_LAYOUTS)[number];

/** В каком углу карточка. */
export const SITE_CONSENT_BANNER_SIDES = ["left", "right"] as const;
export type SiteConsentBannerSide = (typeof SITE_CONSENT_BANNER_SIDES)[number];

/** Подписи для кабинета (`OptionCards`, `Segmented`); «Что изменено» берёт их строчными. */
export const SITE_CONSENT_BANNER_LAYOUT_LABELS: Readonly<Record<SiteConsentBannerLayout, string>> = {
  bar: "Полоса внизу",
  card: "Карточка в углу",
};

export const SITE_CONSENT_BANNER_SIDE_LABELS: Readonly<Record<SiteConsentBannerSide, string>> = {
  left: "Слева",
  right: "Справа",
};

export const siteConsentBannerSchema = z.strictObject({
  layout: z.enum(SITE_CONSENT_BANNER_LAYOUTS),
  /** Ширина карточки на ПК, px (число, а не ступень — владелец рисует сам). */
  cardWidth: z.number().int().min(L.cardWidth.min).max(L.cardWidth.max),
  cardSide: z.enum(SITE_CONSENT_BANNER_SIDES),
  /** Заливка баннера: роль-поверхность или свой цвет (как у корзины). */
  fill: siteSurfaceFillSchema,
  /** Цвет текста; «Авто» — по тону заливки. */
  text: siteAutoTextColorSchema,
});
export type SiteConsentBanner = z.infer<typeof siteConsentBannerSchema>;

/** Нынешняя полоса CookieBanner.tsx: плашка + «Авто» (= «Текст» на светлой плашке). */
export const DEFAULT_SITE_CONSENT_BANNER: SiteConsentBanner = deepFreeze({
  layout: "bar",
  cardWidth: L.cardWidth.default,
  cardSide: "right",
  fill: { kind: "role", role: "panelBg" },
  text: { kind: "auto" },
});

/**
 * Вид, который витрина рисует на экране данной ширины: уже `wideFrom` —
 * всегда полоса (SB134), на `/checkout`, `/order-confirm` и в ЛК — тоже
 * полоса (компактная; это решает витрина по адресу, `isCompactConsentPath`).
 */
export function consentBannerEffectiveLayout(
  banner: Pick<SiteConsentBanner, "layout">,
  viewportWidth: number,
): SiteConsentBannerLayout {
  return viewportWidth < L.wideFrom ? "bar" : banner.layout;
}

/** Цвет текста как CSS: «Авто» — по тону заливки, роль — живая переменная темы, свой — hex. */
function textCss(color: SiteAutoTextColor, tone: "light" | "dark"): string {
  if (color.kind === "auto") return tone === "dark" ? "var(--site-on-dark)" : "var(--site-text)";
  return colorRefCss(color);
}

/**
 * Переменные баннера для `style` его корня (витрина и канва). Не входят в
 * `buildSiteThemeVars`: прежние переменные темы должны остаться побайтно
 * (золотой тест корзины), а баннер — один элемент. Роли — ссылкой на
 * переменные темы, поэтому превью темы на канве перекрашивает баннер само.
 *   --site-consent-bg      заливка;
 *   --site-consent-fg      текст;
 *   --site-consent-line    линия сверху у полосы (по тону);
 *   --site-consent-card-w  ширина карточки на ПК, px.
 */
export function consentBannerCssVars(
  banner: SiteConsentBanner,
  theme: { readonly colors: SiteColors },
): Array<[string, string]> {
  const { background, tone } = resolveSurfaceFill(banner.fill, theme);
  // Как `surfaceFillCssValue` (css.ts): роль — переменная темы, свой — непрозрачный hex.
  // css.ts не импортируем: он читает schema.ts, а schema.ts — этот модуль (цикл).
  const bg = banner.fill.kind === "role" ? `var(${siteColorVar(banner.fill.role)})` : background;
  return [
    ["--site-consent-bg", bg],
    ["--site-consent-fg", textCss(banner.text, tone)],
    ["--site-consent-line", tone === "dark" ? "var(--site-line-on-dark)" : "var(--site-line)"],
    ["--site-consent-card-w", `${banner.cardWidth}px`],
  ];
}

// ── Предупреждения (не запреты, SB120) ──

export type SiteConsentBannerWarningCode = "consent_banner_text_contrast";

export interface SiteConsentBannerWarning {
  code: SiteConsentBannerWarningCode;
  /** Путь поля в теме — подсветка в кабинете. */
  path: string;
  /** Контраст до десятых с отбрасыванием. */
  value?: number;
}

export const SITE_CONSENT_BANNER_WARNING_MESSAGES: Readonly<Record<SiteConsentBannerWarningCode, string>> = {
  consent_banner_text_contrast: "Контраст текста баннера с заливкой ниже 4,5:1 — возьмите читаемый цвет.",
};

/**
 * Предупреждения вида баннера для кабинета и окна публикации (SB134:
 * контраст ниже 4,5:1 — предупреждение). Правило то же, что у корзины:
 * пары, которые уже проверяют правила контраста темы, не повторяются.
 */
export function consentBannerWarnings(
  banner: SiteConsentBanner,
  theme: { readonly colors: SiteColors },
): SiteConsentBannerWarning[] {
  const ratio = autoTextOnFillRatio(banner.text, banner.fill, theme);
  if (ratio === null || ratio >= SITE_CONTRAST_TEXT_MIN) return [];
  return [{ code: "consent_banner_text_contrast", path: "consentBanner.text", value: floorContrastTenth(ratio) }];
}
